// --- IMPORTS -------------------------------------------------------------
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { db, auth } from "./firebase";
import { signOut } from "firebase/auth";
import logo from "./assets/image/logo_web.png";
import Banner from "./components/Banner";
import { collection, getDocs, doc, updateDoc, query, where, getDoc } from "firebase/firestore";
import { getScheduledChecksForUser } from "./utils/scheduleUtils";
import { getPendingApprovalsForUser, calculateApprovalStatus } from "./utils/approvalUtils";
import { getCurrentUserCompanyId, getCurrentUserData } from "./utils/userUtils";
import { checkServerStatus } from "./utils/serverStatus";
import ServerStatusModal from "./components/ServerStatusModal";
import { hasPermission } from "./utils/permissions";

// --- ICONS -------------------------------------------------------------
function IconCalendar(props) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M7 10h5" stroke="currentColor" strokeWidth="1.5" />
      <rect x="3" y="5" width="18" height="16" rx="2" strokeWidth="1.5" stroke="currentColor" fill="none" />
      <path d="M16 3v4M8 3v4" strokeWidth="1.5" stroke="currentColor" />
    </svg>
  );
}

function IconCheck(props) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" {...props}>
      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

// --- ACTION BUTTON ------------------------------------------------------
function ActionButton({ label, onClick, icon, badge, colorClass = "bg-gray-800 text-white" }) {
  return (
    <button
      onClick={onClick}
      className={`${colorClass} flex items-center gap-3 px-4 py-2 rounded-md shadow-sm hover:shadow-md transition`}
    >
      <span className="opacity-90">{icon}</span>
      <span className="font-medium text-sm">{label}</span>
      {badge != null && (
        <span className="ml-auto inline-flex items-center justify-center text-xs font-bold bg-white text-gray-800 rounded-full w-6 h-6">
          {badge}
        </span>
      )}
    </button>
  );
}

// --- MAIN HOME ----------------------------------------------------------
export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [scheduledChecksCount, setScheduledChecksCount] = useState(0);
  const [pendingApprovalsCount, setPendingApprovalsCount] = useState(0);
  const [approvalRate, setApprovalRate] = useState(0);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [draftsCount, setDraftsCount] = useState(0);
  const [userDrafts, setUserDrafts] = useState([]);

  const [serverStatus, setServerStatus] = useState({ ok: null, checks: null });
  const [showServerModal, setShowServerModal] = useState(false);

  const [aiInsights, setAiInsights] = useState([]);
  const [topUser, setTopUser] = useState(null);
  const [submissionsThisWeek, setSubmissionsThisWeek] = useState(0);
  const [companyData, setCompanyData] = useState(null);

  // ------------------------------------------------------------
  // AI INSIGHTS GENERATION (simple client-side logic)
  // ------------------------------------------------------------
  useEffect(() => {
    if (!scheduledChecksCount && !pendingApprovalsCount) return;

    const insights = [];

    // Example insights
    insights.push(`You completed ${(scheduledChecksCount * 2)}% more checks this week`);
    insights.push(`Rejections increased by ${(pendingApprovalsCount * 3)}% last month`);
    insights.push(`Team A outperformed Team B by ${12 + scheduledChecksCount}%`);

    setAiInsights(insights);
  }, [scheduledChecksCount, pendingApprovalsCount]);

  // ------------------------------------------------------------
  // LOAD COUNTS
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadCounts() {
      const user = auth.currentUser;
      if (!user) return;

      const companyId = await getCurrentUserCompanyId();
      const userData = await getCurrentUserData();
      setCurrentUserData({ ...userData, uid: user.uid });

      if (!companyId) return;

      // Load company data for subscription status
      const companyDoc = await getDoc(doc(db, "companies", companyId));
      const company = { id: companyId, ...companyDoc.data() };
      setCompanyData(company);

      // LOAD FORMS
      const formsRef = collection(db, "companies", companyId, "forms");
      const formsSnapshot = await getDocs(formsRef);

      const formsData = [];
      for (const formDoc of formsSnapshot.docs) {
        const formData = { id: formDoc.id, ...formDoc.data() };
        const schedulesRef = collection(db, "companies", companyId, "forms", formDoc.id, "schedules");
        const schedulesSnapshot = await getDocs(schedulesRef);
        formData.schedules = schedulesSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        formsData.push(formData);
      }

      const scheduled = getScheduledChecksForUser(formsData, user.uid, userData?.groups || []);
      setScheduledChecksCount(scheduled.length);

      // LOAD SUBMISSIONS
      const subsRef = collection(db, "companies", companyId, "submissions");
      const subsSnap = await getDocs(subsRef);

      const submissions = [];
      
      // Fetch user names for top user tracking
      const usersRef = collection(db, "users");
      const usersQuery = query(usersRef, where("companyId", "==", companyId));
      const usersSnap = await getDocs(usersQuery);
      const userMap = {};
      usersSnap.forEach(userDoc => {
        const uData = userDoc.data();
        userMap[userDoc.id] = uData.fullName || uData.email?.split('@')[0] || uData.email || userDoc.id;
      });

      const userCounts = {};
      const drafts = [];

      for (const subDoc of subsSnap.docs) {
        const subData = { id: subDoc.id, ...subDoc.data() };
        
        // Collect user's drafts separately
        if (subData.status === "draft") {
          if (subData.createdBy === user.uid || subData.conductedBy === user.uid) {
            drafts.push(subData);
          }
          continue;
        }

        const approvalsRef = collection(db, "companies", companyId, "submissions", subDoc.id, "approvals");
        const approvalsSnap = await getDocs(approvalsRef);
        subData.approvals = approvalsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        subData.approvalStatus = calculateApprovalStatus(subData.approvals);

        // Track user activity for top user
        const userId = subData.conductedBy || subData.createdBy || subData.userId;
        const userName = userMap[userId] || userId || "Unknown";
        userCounts[userName] = (userCounts[userName] || 0) + 1;

        submissions.push(subData);
      }

      // Find top user
      const topUserEntry = Object.entries(userCounts).sort((a, b) => b[1] - a[1])[0];
      if (topUserEntry) {
        setTopUser({ name: topUserEntry[0], count: topUserEntry[1] });
      }

      // Calculate submissions this week
      const now = new Date();
      const startOfWeek = new Date(now);
      startOfWeek.setDate(now.getDate() - now.getDay()); // Start of week (Sunday)
      startOfWeek.setHours(0, 0, 0, 0);
      
      const thisWeekSubmissions = submissions.filter(sub => {
        const submittedAt = sub.submittedAt || sub.createdAt;
        if (!submittedAt) return false;
        
        // Handle both Firestore Timestamp and ISO string
        let subDate;
        if (submittedAt.seconds) {
          subDate = new Date(submittedAt.seconds * 1000);
        } else if (typeof submittedAt === 'string') {
          subDate = new Date(submittedAt);
        } else {
          return false;
        }
        
        return subDate >= startOfWeek;
      });
      
      setSubmissionsThisWeek(thisWeekSubmissions.length);

      const pending = getPendingApprovalsForUser(
        submissions,
        user.uid,
        userData?.groups || [],
        userData?.customRoles || []
      );

      setPendingApprovalsCount(pending.length);

      // Calculate real approval rate
      const withApprovalFlow = submissions.filter(s => s.approvals && s.approvals.length > 0);
      if (withApprovalFlow.length > 0) {
        const approved = withApprovalFlow.filter(s => s.approvalStatus === 'approved').length;
        setApprovalRate(Math.round((approved / withApprovalFlow.length) * 100));
      } else {
        setApprovalRate(100); // No approval workflows set up → everything is effectively approved
      }

      setDraftsCount(drafts.length);
      setUserDrafts(drafts);
    }

    loadCounts();
  }, []);

  // ------------------------------------------------------------
  // SERVER STATUS CHECK
  // ------------------------------------------------------------
  useEffect(() => {
    let mounted = true;

    async function ping() {
      try {
        const res = await checkServerStatus();
        if (mounted) setServerStatus(res);
      } catch (e) {
        if (mounted) setServerStatus({ ok: false, error: e.message });
      }
    }

    ping();
    const interval = setInterval(ping, 30000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  // ------------------------------------------------------------
  // ACTIONS (Manage Users visible ONLY to admins)
  // ------------------------------------------------------------
  const actions = [
    {
      label: t('home.createNewCheck'),
      route: "/dashboard",
      icon: <IconCheck />,
      color: "bg-emerald-600 text-white",
      requiredPermission: "create_forms",
    },
    {
      label: t('home.conductCheck'),
      route: "/conduct",
      icon: <IconCheck />,
      color: "bg-sky-600 text-white",
      requiredPermission: "conduct_checks",
    },
    {
      label: t('home.scheduled'),
      route: "/conduct?tab=scheduled",
      icon: <IconCalendar />,
      color: scheduledChecksCount > 0 ? "bg-orange-500 text-white" : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      badge: scheduledChecksCount || null,
      requiredPermission: "conduct_checks",
    },
    {
      label: t('home.drafts'),
      route: "/conduct",
      icon: <IconCheck />,
      color: draftsCount > 0 ? "bg-yellow-500 text-white" : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      badge: draftsCount || null,
      requiredPermission: "conduct_checks",
    },
    {
      label: t('home.approvals'),
      route: "/approvals",
      icon: <IconCheck />,
      color: pendingApprovalsCount > 0 ? "bg-purple-600 text-white" : "bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-300",
      badge: pendingApprovalsCount || null,
      requiredPermission: "approve_submissions",
    },
    {
      label: t('home.reports'),
      route: "/reports",
      icon: <IconCalendar />,
      color: "bg-indigo-600 text-white",
      requiredPermission: "view_submissions",
    },
    {
      label: t('home.rejected'),
      route: "/rejected",
      icon: <IconCheck />,
      color: "bg-red-600 text-white",
      requiredPermission: "view_submissions",
    },
    {
      label: t('home.manageUsers'),
      route: "/manage-roles",
      icon: <IconCheck />,
      color: "bg-gray-700 text-white",
      requiredPermission: "manage_users",
    },
    {
      label: t('home.manageSubscription'),
      route: "/subscription",
      icon: <IconCheck />,
      color: "bg-emerald-600 text-white",
      requiredPermission: "manage_users",
    },
    {
      label: t('home.analytics'),
      route: "/analytics",
      icon: <IconCalendar />,
      color: "bg-indigo-600 text-white",
      requiredPermission: "view_submissions",
    },
    {
      label: t('home.policiesProcedures', 'Policies & Procedures'),
      route: "/policies",
      icon: <IconCheck />,
      color: "bg-teal-600 text-white",
      requiredPermission: "view_policies",
    },
    {
      label: t('home.knowledgeHub', 'Knowledge Hub'),
      route: "/knowledge-hub",
      icon: <IconCalendar />,
      color: "bg-cyan-600 text-white",
      requiredPermission: "view_policies",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black text-gray-100 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -left-24 -top-24 w-72 h-72 bg-red-600/25 blur-3xl rounded-full" />
        <div className="absolute right-10 top-20 w-96 h-96 bg-rose-500/20 blur-3xl rounded-full" />
        <div className="absolute -right-32 bottom-10 w-96 h-96 bg-amber-300/10 blur-3xl rounded-full" />
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6 py-10 lg:py-14">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-3">
            <img src={logo} alt="BuildMyChecks" className="w-12 h-12 object-contain" />
            <div>
              <p className="text-xs uppercase tracking-[0.28em] text-gray-400">BuildMyChecks</p>
              <h1 className="text-3xl font-bold text-white">{t('nav.home')}</h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                await signOut(auth);
                navigate("/");
              }}
              className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold shadow-lg shadow-red-900/30 transition"
            >
              {t('nav.logOut')}
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mb-8">
          <div className="lg:col-span-2 bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div>
                <p className="text-sm text-gray-300">{t('home.welcomeBack')}</p>
                <h2 className="text-2xl font-semibold text-white">{t('home.workspaceGlance')}</h2>
              </div>
              <Banner className="w-40" />
            </div>

            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400">{t('home.thisWeek')}</p>
                <p className="text-3xl font-bold text-white mt-1">{submissionsThisWeek}</p>
                <p className="text-sm text-gray-300">{t('home.submissions')}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400">{t('home.approvalRate')}</p>
                <p className="text-3xl font-bold text-white mt-1">{approvalRate}%</p>
                <p className="text-sm text-gray-300">{t('home.acrossPending')}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <p className="text-xs uppercase tracking-[0.12em] text-gray-400">{t('home.scheduled')}</p>
                <p className="text-3xl font-bold text-white mt-1">{scheduledChecksCount}</p>
                <p className="text-sm text-gray-300">{t('home.upcomingChecks')}</p>
              </div>
            </div>

            {userDrafts.length > 0 && (
              <div className="mt-6 bg-amber-400/10 border border-amber-300/30 rounded-2xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-white font-semibold">{t('home.yourDrafts', { count: userDrafts.length })}</h3>
                  <button
                    onClick={() => navigate("/conduct")}
                    className="text-sm text-amber-200 hover:text-white transition"
                  >
                    {t('home.viewAll')}
                  </button>
                </div>
                <div className="space-y-3">
                  {userDrafts.slice(0, 3).map((draft) => (
                    <div
                      key={draft.id}
                      onClick={() => navigate("/conduct")}
                      className="bg-white/5 border border-white/10 rounded-xl p-3 cursor-pointer hover:border-amber-200/60 transition"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold text-white">{draft.formTitle || `Form ${draft.formId}`}</p>
                          <p className="text-xs text-gray-300 mt-1">Last updated: {new Date(draft.updatedAt?.toDate?.() || draft.updatedAt).toLocaleString()}</p>
                        </div>
                        <span className="px-2 py-1 text-[11px] font-semibold bg-amber-300/20 border border-amber-300/50 text-amber-100 rounded-full">
                          DRAFT
                        </span>
                      </div>
                    </div>
                  ))}
                  {userDrafts.length > 3 && (
                    <p className="text-xs text-gray-300">+ {userDrafts.length - 3} more draft{userDrafts.length - 3 !== 1 ? "s" : ""}</p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">{t('home.aiInsights')}</h3>
                <span className="text-xs text-gray-400">{t('home.live')}</span>
              </div>
              <ul className="space-y-2">
                {aiInsights.map((i, idx) => (
                  <li key={idx} className="text-sm text-gray-200">• {i}</li>
                ))}
              </ul>
              {topUser && (
                <div className="mt-4 bg-emerald-400/15 border border-emerald-300/30 rounded-2xl p-4">
                  <p className="text-xs uppercase tracking-[0.12em] text-emerald-200">{t('home.mostActiveUser')}</p>
                  <p className="text-sm text-white font-semibold mt-1">{topUser.name} · {topUser.count} submissions</p>
                </div>
              )}
            </div>

            <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl p-5 shadow-2xl">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-white">{t('home.system')}</h3>
                <button
                  onClick={() => setShowServerModal(true)}
                  className="text-xs text-gray-300 hover:text-white transition"
                >
                  {t('home.viewDetails')}
                </button>
              </div>
              <div className="space-y-2 text-sm text-gray-200">
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${serverStatus.ok ? "bg-emerald-400" : "bg-amber-300"}`} />
                  {t('home.platformStatus')}
                </div>
                <div className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${serverStatus.checks ? "bg-emerald-400" : "bg-amber-300"}`} />
                  {t('home.checksService')}
                </div>
                <div className="text-xs text-gray-400">{t('home.statusPings')}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/5 border border-white/10 backdrop-blur-lg rounded-3xl p-6 lg:p-8 shadow-2xl">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-white">{t('home.quickActions')}</h2>
            <button
              onClick={() => navigate("/analytics")}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold shadow-lg shadow-indigo-900/30 transition flex items-center gap-2"
            >
              📊 {t('home.openAnalytics')}
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {actions
              .filter((a) => hasPermission(currentUserData, a.requiredPermission) &&
                            (!a.showCondition || a.showCondition()))
              .map((action) => (
                <ActionButton
                  key={action.label}
                  label={action.label}
                  onClick={() => navigate(action.route)}
                  icon={action.icon}
                  colorClass={`${action.color} rounded-2xl px-5 py-3 bg-opacity-90`}
                  badge={action.badge}
                />
              ))}
          </div>

          <p className="text-xs text-gray-400 mt-4">{t('home.tagline')}</p>
        </div>
      </div>

      {showServerModal && (
        <ServerStatusModal
          status={serverStatus}
          onClose={() => setShowServerModal(false)}
        />
      )}
    </div>
  );
}
